import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

/* Palette */
const C = {
    black:        '#1a1a1a',
    darkGray:     '#333333',
    midGray:      '#666666',
    silver:       '#cccccc',
    accent:       '#8b0000',
    headerBg:     '#1c2b4a',
    sectionBg:    '#dde3ec',
    white:        '#ffffff',
    sigBorder:    '#d32f2f', // bordo rosso dei riquadri firma cliente
};

const styles = StyleSheet.create({
    page: {
        fontSize: 9,
        fontFamily: 'Times-Roman',
        lineHeight: 1.45,
        color: C.black,
        // Spazio sotto il footer per il numero pagina fisso.
        paddingBottom: 18,
    },

    /* Header nello stile di MandatoPDF */
    header: {
        paddingTop: 0,
        paddingBottom: 4,
        paddingHorizontal: 35,
    },
    headerBanner: {
        width: '100%',
        objectFit: 'contain',
    },
    headerInner: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: 10,
        paddingBottom: 6,
    },
    logo: {
        width: 50,
        height: 40,
        objectFit: 'contain',
    },
    headerTextBlock: {
        flex: 1,
        paddingLeft: 10,
    },
    headerAgency: {
        fontSize: 12,
        color: C.black,
        letterSpacing: 0.3,
    },
    headerSub: {
        fontSize: 7.5,
        color: C.midGray,
        marginTop: 1,
    },
    headerAccentBar: {
        height: 2,
        backgroundColor: C.accent,
    },
    headerMeta: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 35,
        paddingTop: 3,
        paddingBottom: 3,
        borderTopWidth: 0.5,
        borderTopColor: C.silver,
    },
    headerLabel: {
        fontSize: 7,
        color: C.midGray,
        fontStyle: 'italic',
    },
    headerPageNum: {
        fontSize: 7,
        color: C.midGray,
        textAlign: 'right',
    },

    /* Footer non fixed, spinto in fondo con marginTop: 'auto' come in BonificaPDF/ConsulenzaPDF.
       Con position fixed OTP trovava l'ancora #otp_pp{n}# in alto invece che nella casella. */
    footer: {
        marginTop: 'auto',
        marginHorizontal: 35,
        borderTopWidth: 0.5,
        borderTopColor: C.silver,
        paddingTop: 6,
        paddingBottom: 4,
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    footerCopyright: {
        fontSize: 6.5,
        color: C.midGray,
        fontStyle: 'italic',
        textAlign: 'left',
        flex: 1,
    },
    footerSignature: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginLeft: 16,
        minWidth: 130,
    },
    footerSignatureLabel: {
        fontSize: 7,
        color: C.midGray,
        marginBottom: 3,
    },
    footerSignatureLine: {
        width: 130,
        height: 26,
        borderWidth: 0.75,
        borderColor: C.sigBorder,
        backgroundColor: '#fafafa',
    },
    // Numero pagina fisso, centrato in basso e separato dalla casella firma
    // (in basso a destra) per non confonderlo con l'ancora OTP.
    footerPageNum: {
        position: 'absolute',
        bottom: 8,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 8,
        color: '#888888',
    },

    /* Intestazione documento */
    docTitleBar: {
        marginTop: 8,
        marginBottom: 6,
        paddingHorizontal: 35,
    },
    docTitleMain: {
        fontSize: 11,
        fontFamily: 'Times-Roman',
        color: C.headerBg,
        letterSpacing: 0.3,
        marginBottom: 2,
        textTransform: 'uppercase',
    },
    docTitleSub: {
        fontSize: 8,
        color: C.midGray,
        fontStyle: 'italic',
    },

    contentBox: {
        paddingHorizontal: 35,
    },

    sectionTitle: {
        backgroundColor: C.sectionBg,
        borderLeftWidth: 3,
        borderLeftColor: C.headerBg,
        paddingVertical: 4,
        paddingHorizontal: 8,
        marginTop: 9,
        marginBottom: 5,
        fontSize: 9,
        color: C.headerBg,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },

    paragraph: {
        marginBottom: 5,
        textAlign: 'justify',
        fontSize: 9,
        lineHeight: 1.5,
        color: C.black,
    },

    bulletRow: {
        flexDirection: 'row',
        marginBottom: 3,
        paddingLeft: 6,
    },
    bullet: {
        width: 9,
        fontSize: 9,
        color: C.headerBg,
    },
    bulletText: {
        flex: 1,
        fontSize: 9,
        textAlign: 'justify',
        lineHeight: 1.45,
    },

    italic: { fontStyle: 'italic', color: C.midGray, fontSize: 8.5 },
    bold: { fontFamily: 'Times-Roman' },
});

/* Interfaccia */

interface SettingsLike {
    agencyName?: string;
    address?: string;
    city?: string;
    phone?: string;
    email?: string;
    vatNumber?: string;
    website?: string;
    logoUrl?: string;
    mandatoHeaderUrl?: string;
    mandatoHeaderImageUrl?: string;
    pec?: string;
}

interface Props {
    settings: SettingsLike;
    /** Sedi opzionali da elencare in intestazione (es. da SedeService) */
    sedi?: { name?: string; address?: string; citta?: string; cap?: string; phone?: string }[];
}

/* Helpers */

const isSupportedImageUrl = (url?: string) =>
    !!url && !url.startsWith('data:image/svg');

const formatAggiornamento = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

/* Header / footer */

const PageHeader: React.FC<{ settings: SettingsLike; sedi?: Props['sedi'] }> = ({ settings, sedi }) => {
    // Stessa intestazione del mandato (di cui l'informativa è allegato), senza ripiego sull'header dei report.
    const rawBanner = settings?.mandatoHeaderUrl || settings?.mandatoHeaderImageUrl;
    const bannerUrl = isSupportedImageUrl(rawBanner) ? rawBanner : undefined;
    const logoUrl   = isSupportedImageUrl(settings?.logoUrl) ? settings?.logoUrl : undefined;
    const agencyName = settings?.agencyName || '—';
    const vat = settings?.vatNumber ? ` - P. IVA: ${settings.vatNumber}` : '';

    const sediLines: string[] = [];
    if (sedi && sedi.length > 0) {
        for (const s of sedi) {
            const parts = [s.name, s.address, [s.cap, s.citta].filter(Boolean).join(' '), s.phone].filter(Boolean);
            if (parts.length) sediLines.push(parts.join(' - '));
        }
    } else if (settings?.address || settings?.city || settings?.phone) {
        sediLines.push([settings.address, settings.city, settings.phone].filter(Boolean).join(' - '));
    }

    const contattiLine = [
        settings?.website,
        settings?.email ? `e-mail: ${settings.email}` : '',
        settings?.pec ? `pec: ${settings.pec}` : '',
    ].filter(Boolean).join('  ·  ');

    return (
        <View style={styles.header} fixed>
            {bannerUrl ? (
                <Image src={bannerUrl} style={styles.headerBanner} />
            ) : (
                <View style={styles.headerInner}>
                    {logoUrl && <Image src={logoUrl} style={styles.logo} />}
                    <View style={styles.headerTextBlock}>
                        <Text style={styles.headerAgency}>{agencyName}{vat}</Text>
                        {sediLines.map((l, i) => (
                            <Text key={i} style={styles.headerSub}>{l}</Text>
                        ))}
                        {contattiLine ? <Text style={styles.headerSub}>{contattiLine}</Text> : null}
                    </View>
                </View>
            )}
            <View style={styles.headerAccentBar} />
            <View style={styles.headerMeta}>
                <Text style={styles.headerLabel}>Sistema Gestione Privacy</Text>
                <Text
                    style={styles.headerPageNum}
                    render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`}
                />
            </View>
        </View>
    );
};

const PageFooter: React.FC<{ agencyName: string; pageNum: number }> = ({ agencyName, pageNum }) => (
    <View style={styles.footer}>
        <Text style={styles.footerCopyright}>
            Il presente documento è redatto e mantenuto da {agencyName} ad uso esclusivo del Mandante. Sono proibite la cessione a terzi, nonché l'utilizzo o la copia, anche parziali, da parte di soggetti non autorizzati. La riproduzione è ammessa solo se completa del presente riquadro.
        </Text>
        <View style={styles.footerSignature}>
            <Text style={styles.footerSignatureLabel}>Firma Mandante:</Text>
            <View style={styles.footerSignatureLine}>
                <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{`#otp_pp${pageNum}#`}</Text>
            </View>
        </View>
    </View>
);

/* Componenti base */

const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text style={styles.sectionTitle}>{children}</Text>
);

const Para: React.FC<{ children: React.ReactNode; style?: any }> = ({ children, style }) => (
    <Text style={[styles.paragraph, style]}>{children}</Text>
);

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <View style={styles.bulletRow}>
        <Text style={styles.bullet}>•</Text>
        <Text style={styles.bulletText}>{children}</Text>
    </View>
);

/* Documento */

export const InformativaPrivacyPDF: React.FC<Props> = ({ settings, sedi }) => {
    const agencyName = settings?.agencyName || 'Agenzia';
    const indirizzoSede = [settings?.address, settings?.city].filter(Boolean).join(' - ');
    const aggData = formatAggiornamento();

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <PageHeader settings={settings} sedi={sedi} />

                <View style={styles.docTitleBar}>
                    <Text style={styles.docTitleMain}>
                        Trattamento dei dati personali relativi ai mandanti
                    </Text>
                    <Text style={styles.docTitleSub}>
                        (di seguito "Mandante" o "Interessato") · Agg. {aggData} · INFORMATIVA ai sensi degli artt. 13 e 14 Reg. UE 2016/679
                    </Text>
                </View>

                <View style={styles.contentBox}>

                    <Para>
                        Secondo il Regolamento UE 2016/679, che conferma quanto sancito dalla normativa italiana già dal dicembre 2011, i dati che riguardano persone giuridiche, enti ed associazioni non sono "dati personali"; di conseguenza per il loro trattamento non sarebbe necessario ottemperare agli obblighi previsti dalla normativa vigente in tema di tutela dei dati personali. Tuttavia, in considerazione del fatto che tra i Mandanti figurano soggetti che rientrano nella definizione di "persone fisiche" (professionisti, ditte individuali, ecc.) e che nell'ambito dei rapporti intercorrenti possono essere trattati dati personali relativi a persone fisiche che hanno rapporti con il Mandante (rappresentanti, dipendenti, collaboratori, ecc.), <Text style={styles.bold}>{agencyName}</Text> mantiene sempre disponibile il presente documento, in cui anche i soggetti esclusi dall'ambito di tutela della normativa in parola potranno riconoscere le politiche adottate nel trattamento delle informazioni e dati personali, che sarà sempre improntato ai principi di correttezza, liceità e trasparenza nel pieno rispetto del diritto alla riservatezza.
                    </Para>

                    {/* 1. Quali dati */}
                    <Section>1. Quali dati vengono trattati</Section>
                    <Para>Verranno trattati esclusivamente dati pertinenti e non eccedenti rispetto alle finalità di seguito elencate.</Para>
                    <Bullet>Dati ed informazioni fornite direttamente dal Mandante o da soggetti da questi delegati e/o relativi al rapporto contrattuale instaurato con l'Agenzia ed alle attività da questa svolte su incarico del Mandante stesso – dati amministrativo-fiscali.</Bullet>
                    <Bullet>Dati forniti dal sottoscrittore del mandato (Mandante o soggetto che agisce in sua rappresentanza): nome, cognome, estremi di un documento d'identità, recapiti (indirizzo, telefono, e-mail, ecc.), informazioni sul sinistro.</Bullet>
                    <Bullet>Dati/documenti forniti dall'Interessato, direttamente o per interposta persona, all'atto del conferimento di mandato e nel corso dei successivi contatti avuti con {agencyName}.</Bullet>
                    <Bullet>Dati relativi a informative precontrattuali (tra cui dati liberamente acquisibili da fonti pubbliche o pubblicamente accessibili).</Bullet>
                    <Bullet>Altri dati provenienti da fonti legittimamente accessibili in relazione alla natura delle attività ed alle finalità di seguito indicate, o da liste/documenti acquisite/i da soggetti terzi autorizzati in virtù di specifiche norme di legge (es. C.C.I.A.A.).</Bullet>
                    <Bullet>Dati acquisiti nel corso delle attività effettuate su mandato dell'interessato stesso.</Bullet>
                    <Para>
                        Tra i dati sopra menzionati potranno essere compresi, solo se pertinenti e necessari in relazione alle finalità di seguito indicate, anche dati relativi a condanne penali e reati e/o "categorie particolari di dati" (art. 9 c. 1 Reg. UE 2016/679: dati che rivelino origine razziale o etnica, opinioni politiche, convinzioni religiose o filosofiche, appartenenza sindacale, dati genetici, biometrici, relativi alla salute o alla vita sessuale o all'orientamento sessuale). Riguardo a questo punto, particolare attenzione viene prestata nell'acquisire solamente i dati e nell'effettuare esclusivamente i trattamenti necessari a soddisfare legittime richieste degli interessati.
                    </Para>

                    {/* 2. Origine */}
                    <Section>2. Origine dei dati</Section>
                    <Para>
                        L'assunzione e l'aggiornamento dei dati connessi alla gestione dei rapporti amministrativo-contrattuali vengono effettuati di norma tramite il Mandante stesso e/o da pubblici registri, elenchi, atti o documenti conoscibili da chiunque o da liste/documenti acquisite/i da soggetti terzi autorizzati in virtù di specifiche norme di legge (es. C.C.I.A.A.) o dal Mandante stesso. Altri dati possono essere acquisiti:
                    </Para>
                    <Bullet>da fonti legittimamente accessibili in relazione alla natura delle attività investigative affidate all'Agenzia;</Bullet>
                    <Bullet>con strumenti e tecniche impiegate da {agencyName} secondo quanto consentito dalla normativa di settore (D.M. 269/2010).</Bullet>

                </View>
                <PageFooter agencyName={agencyName} pageNum={1} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />
            </Page>

            {/* Pagina 2: sezioni 3 (finalità) e 4 (modalità e conservazione), da sole per leggibilità. */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={settings} sedi={sedi} />
                <View style={styles.contentBox}>

                    {/* 3. Finalità */}
                    <Section>3. Perché vengono trattati i dati – Finalità del trattamento e basi giuridiche</Section>
                    <Para>
                        I trattamenti a cui saranno sottoposti i dati personali, richiesti o acquisiti sia preventivamente all'instaurazione del rapporto commerciale, ovvero nel corso o dopo la cessazione dello stesso, hanno le seguenti finalità per ognuna delle quali viene indicata tra parentesi la "base giuridica" che la rende possibile (artt. 6 e 9 Reg. UE 2016/679):
                    </Para>
                    <Bullet>
                        <Text style={styles.bold}>a. </Text>espletamento degli obblighi contrattuali, contabili e fiscali derivanti dal rapporto commerciale di cui è parte {agencyName}; adempiere ad obblighi derivanti da leggi, norme e regolamenti comunitari; adempimento disposizioni impartite dall'Autorità Giudiziaria, da quella finanziaria, dagli istituti assicurativi <Text style={styles.italic}>(art. 6 c.1 lett. b-c – art. 9 c. 2 lett. b-h)</Text>; corretta e completa esecuzione dell'incarico ricevuto – soddisfare le richieste dell'interessato ponendolo in condizioni di tutelare un proprio diritto o legittimo interesse <Text style={styles.italic}>(art. 6 c.1 lett. f – art. 9 c. 2 lett. f)</Text>.
                    </Bullet>
                    <Bullet>
                        <Text style={styles.bold}>b. </Text>finalità strettamente connesse e strumentali alla gestione dei rapporti con la clientela o con i fornitori, sempre in relazione alla gestione del mandato e delle attività da esso disciplinate <Text style={styles.italic}>(art. 6 c.1 lett. b-c-f)</Text>.
                    </Bullet>
                    <Bullet>
                        <Text style={styles.bold}>c. </Text>valutazioni preliminari all'instaurazione di un rapporto di collaborazione o commerciale, acquisizione di informazioni preliminari alla conclusione di un contratto; in particolare per contratti di valore ingente potranno essere acquisite da pubblici registri, elenchi, atti o documenti conoscibili da chiunque informazioni necessarie a valutare l'affidabilità ("informazioni commerciali") <Text style={styles.italic}>(art. 6 c.1 lett. f)</Text>.
                    </Bullet>
                    <Bullet>
                        <Text style={styles.bold}>d. </Text>esigenze di tipo operativo e gestionale interne all'Agenzia quali gestione anagrafiche clienti, indirizzari e calcoli statistici interni <Text style={styles.italic}>(art. 6 c.1 lett. f)</Text>.
                    </Bullet>
                    <Bullet>
                        <Text style={styles.bold}>e. </Text>tutelare un legittimo interesse, accertare, far valere o difendere un diritto <Text style={styles.italic}>(art. 6 c.1 lett. f – art. 9 c. 2 lett. f)</Text>.
                    </Bullet>
                    <Bullet>
                        <Text style={styles.bold}>f. </Text>attività commerciale: marketing, attività promozionali. Salvo diversa indicazione da parte del Cliente, i recapiti, gli indirizzi postali e di posta elettronica forniti potranno essere utilizzati per l'invio di comunicazioni di cortesia e/o di materiale informativo/offerte relative a servizi analoghi a quelli oggetto del rapporto commerciale in essere. Resta inteso che il Cliente ha facoltà di opporsi in qualsiasi momento al suddetto trattamento <Text style={styles.italic}>(art. 6 c.1 lett. f – D.Lgs. 196/2003 art. 130 c.4 – consenso art. 6 c.1 lett. a Reg. UE 2016/679)</Text>.
                    </Bullet>

                    {/* 4. Come trattati */}
                    <Section>4. Come vengono trattati i dati e conservazione</Section>
                    <Para>
                        In relazione alle summenzionate finalità i trattamenti dei dati personali potranno avvenire con strumenti cartacei, informatici e telematici, sempre garantendo la più assoluta riservatezza, pertinenza e non eccedenza rispetto alle finalità sopra descritte.
                    </Para>
                    <Para>
                        I dati di natura contabile ed amministrativa, nonché la documentazione necessaria a dimostrarne la correttezza, verranno conservati per i periodi imposti dalla normativa vigente, in conformità alle norme sulla conservazione della documentazione amministrativa. Gli altri dati, salvo diverse ed ulteriori esigenze, verranno conservati per i tempi consentiti/imposti dalla normativa vigente applicabile alla specifica finalità per cui i dati sono trattati.
                    </Para>
                    <Para>
                        Gli indirizzi di posta elettronica e gli altri recapiti trattati per finalità commerciali verranno conservati per 12 mesi successivi all'ultimo invio/contatto avuto con l'interessato. Salvo diversi accordi o ulteriori esigenze, i dati risultanti dalle attività investigative non verranno conservati oltre pochi giorni successivi alla conclusione delle indagini.
                    </Para>

                </View>
                <PageFooter agencyName={agencyName} pageNum={2} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />
            </Page>

            {/* Pagina 3: sezioni 5-11, soggetti coinvolti, trasferimenti, titolare e diritti. */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={settings} sedi={sedi} />
                <View style={styles.contentBox}>
                    {/* 5. Da chi */}
                    <Section>5. Da chi possono essere trattati</Section>
                    <Para>I dati potranno essere trattati dalle seguenti categorie di incaricati e/o responsabili, sempre e solo nei limiti di quanto effettivamente necessario ad espletare le proprie funzioni:</Para>
                    <Bullet>Direzione aziendale e management;</Bullet>
                    <Bullet>personale addetto alle attività di indagine, verifica, sopralluogo, accertamento, perizie e valutazioni;</Bullet>
                    <Bullet>personale addetto alla manutenzione dei sistemi informatici, con esclusione della consultazione sistematica;</Bullet>
                    <Bullet>altro personale autorizzato dal Titolare nei limiti degli incarichi ricevuti;</Bullet>
                    <Bullet>soggetti (società/professionisti, anche al di fuori dell'Unione Europea ove ciò sia necessario in relazione alla natura ed oggetto del rapporto contrattuale) che collaborano in qualità di Responsabili ex art. 28 Reg. UE 679/2016, o che erogano servizi funzionali, quali: consulenze specialistiche e perizie, assistenza nell'espletamento o esecuzione diretta di adempimenti fiscali/contabili/amministrativi, gestione sistemi informativi, servizi finanziari, verifiche ed attività di indagine, recupero del credito.</Bullet>


                    {/* 6. A chi */}
                    <Section>6. A chi possono essere comunicati</Section>
                    <Para>I dati personali potranno essere comunicati o resi disponibili:</Para>
                    <Bullet>ai soggetti indicati dal Mandante;</Bullet>
                    <Bullet>a soggetti che possono accedere ai dati in forza di disposizione di legge, di regolamento o di normativa comunitaria, nei limiti previsti da tali norme – pubbliche amministrazioni, per lo svolgimento delle funzioni istituzionali;</Bullet>
                    <Bullet>altri Enti/Associazioni/Società/Istituti di credito per finalità connesse all'esecuzione degli obblighi collegati e/o connessi con il rapporto intercorrente;</Bullet>
                    <Bullet>clienti/fornitori in relazione ad obblighi contrattuali e/o di legge e/o per finalità commerciali e di public relations, sia all'interno che all'esterno dell'Unione Europea;</Bullet>
                    <Bullet>soggetti (società/professionisti, anche al di fuori dell'Unione Europea) che partecipano in qualità di Titolari alle attività, o che erogano servizi funzionali (consulenze specialistiche, perizie, verifiche, attività di indagine, servizi finanziari, recupero del credito).</Bullet>
                    <Para>
                        Tutti i soggetti sopra elencati saranno vincolati al pieno rispetto degli obblighi di riservatezza e sicurezza. Le comunicazioni sopra descritte sono limitate ai soli dati necessari all'Ente/ufficio destinatario per l'espletamento dei propri compiti.
                    </Para>

                    {/* 7. Trasferimento estero */}
                    <Section>7. Trasferimento all'estero</Section>
                    <Para>
                        I dati personali potranno essere trasmessi verso soggetti siti anche al di fuori dell'Unione Europea ove ciò sia necessario in relazione al mandato conferito a {agencyName} o dei soggetti coinvolti; in particolare verso il/i Paese/i:
                    </Para>
                    <Bullet>in cui hanno sede i soggetti mandanti;</Bullet>
                    <Bullet>in cui l'oggetto dell'indagine sviluppa i propri effetti;</Bullet>
                    <Bullet>in cui hanno sede soggetti coinvolti nel soddisfacimento delle richieste degli interessati.</Bullet>
                    <Para>
                        Il trasferimento verrà sempre effettuato nel pieno rispetto della normativa e potrà essere effettuato esclusivamente in ricorrenza dei presupposti di legittimità di cui al Capo V del Reg. UE 2016/679.
                    </Para>

                    {/* 8. Diffusione */}
                    <Section>8. Diffusione</Section>
                    <Para>I dati non saranno diffusi da {agencyName}.</Para>

                </View>
                <PageFooter agencyName={agencyName} pageNum={3} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />
            </Page>

            <Page size="A4" style={styles.page}>
                <PageHeader settings={settings} sedi={sedi} />
                <View style={styles.contentBox}>
                    {/* 9. Comunicazione obbligatoria */}
                    <Section>9. Comunicazione e aggiornamento dei dati – obbligatorietà</Section>
                    <Para>
                        La comunicazione e l'aggiornamento dei propri dati ha natura obbligatoria per quanto attiene allo svolgimento di adempimenti contrattuali e fiscali previsti dalle vigenti normative di legge e all'esecuzione degli obblighi derivanti dal contratto. La mancata ottemperanza a tale obbligo da parte del Cliente comporterebbe l'impossibilità per {agencyName} di erogare i servizi o le forniture richieste, nonché di espletare tutte le normali pratiche connesse al rapporto commerciale con l'interessato.
                    </Para>
                    <Para>
                        È invece facoltativa la comunicazione di altri dati eventualmente richiesti (cellulare, indirizzo e-mail, livello soddisfazione, ecc.) non direttamente attinenti ad obblighi contrattuali ma funzionali al perseguimento delle finalità citate. In assenza di tali dati, per cui potrebbe essere richiesto uno specifico consenso, non ci saranno conseguenze salvo l'impossibilità di gestire al meglio il rapporto commerciale in essere.
                    </Para>

                    {/* 10. Titolare */}
                    <Section>10. Titolare del trattamento</Section>
                    <Para>
                        Titolare del trattamento è <Text style={styles.bold}>{agencyName}</Text>{indirizzoSede ? `, con sede in ${indirizzoSede}` : ''}{settings?.vatNumber ? ` – P. IVA ${settings.vatNumber}` : ''}{settings?.email ? ` – e-mail ${settings.email}` : ''}{settings?.pec ? ` – PEC ${settings.pec}` : ''}.
                    </Para>
                    {/* 11. Diritti */}
                    <Section>11. Diritti dell'interessato</Section>
                    <Para>L'interessato ha il diritto:</Para>
                    <Bullet>di chiedere al Titolare l'accesso ai dati personali e la rettifica o la cancellazione degli stessi o la limitazione del trattamento dei dati che lo riguardano e di opporsi al loro trattamento;</Bullet>
                    <Bullet>se il trattamento è effettuato con mezzi automatizzati e sulla base del proprio consenso, di ricevere in un formato strutturato, di uso comune e leggibile da dispositivo automatico i dati personali che lo riguardano e/o di ottenerne la trasmissione diretta ad altro Titolare, se tecnicamente fattibile;</Bullet>
                    <Bullet>di revocare il proprio consenso in qualsiasi momento (senza pregiudicare la liceità del trattamento basata sul consenso prima della revoca);</Bullet>
                    <Bullet>di proporre reclamo a un'autorità di controllo: Garante per la protezione dei dati personali – Piazza di Monte Citorio n. 121, 00186 Roma – Fax (+39) 06.69677.3785 – Centralino (+39) 06.696771 – e-mail garante@gpdp.it – PEC protocollo@pec.gpdp.it.</Bullet>
                    <Para>
                        Per esercitare i propri diritti l'interessato potrà utilizzare i recapiti riportati in intestazione, tenendo presente che non sarà possibile rispondere a richieste pervenute telefonicamente ove non vi sia certezza circa l'identità del richiedente.
                    </Para>

                </View>
                <PageFooter agencyName={agencyName} pageNum={4} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />
            </Page>
        </Document>
    );
};

export default InformativaPrivacyPDF;
